import React from 'react';
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    useTheme as useMuiTheme,
} from '@mui/material';

// Prefix characters that spreadsheet apps interpret as formula openers.
const FORMULA_INJECTION_RE = /^[=+\-@]/;

const sanitizeCsvCell = (val: string): string => {
    const escaped = val.replace(/"/g, '""');
    // Prepend a single quote to neutralise formula injection (M-5).
    return FORMULA_INJECTION_RE.test(escaped) ? `'${escaped}` : escaped;
};

export const downloadCsv = <T extends object>(rows: T[], filename: string): void => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]) as (keyof T)[];
    const csv = [
        headers.join(','),
        ...rows.map(r =>
            headers.map(h => `"${sanitizeCsvCell(String(r[h] ?? ''))}"`).join(',')
        ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

// Caps the open dropdown at ~8 visible rows, scrolling for the rest, instead of growing
// unbounded and running off/behind the page for filters with many options (e.g. Report,
// which lists every distinct CSV filename ever ingested).
const MENU_ITEM_HEIGHT = 36;
const MENU_MAX_VISIBLE_ITEMS = 8;

export const MultiSelectFilter = ({
    label,
    options,
    value,
    onChange,
}: {
    label: string;
    options: string[];
    value: string[];
    onChange: (v: string[]) => void;
}) => (
    <FormControl size="small" fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
            multiple
            value={value}
            onChange={(e) =>
                onChange(
                    typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value
                )
            }
            input={<OutlinedInput label={label} />}
            renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((v) => (
                        <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ))}
                </Box>
            )}
            MenuProps={{
                PaperProps: {
                    style: { maxHeight: MENU_ITEM_HEIGHT * MENU_MAX_VISIBLE_ITEMS + 8 },
                },
            }}
        >
            {options.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                    {opt}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

interface ConfirmActionDialogProps {
    open: boolean;
    title: string;
    message: React.ReactNode;
    caveat?: string;
    confirmLabel: string;
    confirmingLabel?: string;
    confirming?: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

/** Shared destructive-action confirmation dialog — matches the "Remove Member" dialog style
 * (error-colored title, Cancel + error-contained confirm button) used elsewhere in the app,
 * so every irreversible action across the site shares one look. */
export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
    open,
    title,
    message,
    caveat = 'This action cannot be undone.',
    confirmLabel,
    confirmingLabel,
    confirming = false,
    onConfirm,
    onClose,
}) => {
    const muiTheme = useMuiTheme();
    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            sx={{ zIndex: 9999 }}
        >
            <DialogTitle sx={{ color: muiTheme.palette.error.main, fontWeight: 'bold' }}>
                {title}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {caveat}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={confirming}
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={confirming}
                    variant="contained"
                    color="error"
                    sx={{
                        backgroundColor: muiTheme.palette.error.main,
                        '&:hover': { backgroundColor: muiTheme.palette.error.dark },
                        '&:disabled': { backgroundColor: muiTheme.palette.grey[400] },
                    }}
                >
                    {confirming ? (confirmingLabel ?? `${confirmLabel}...`) : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
